import { z, type ZodTypeAny } from 'zod';
import type { IssueTypeConfig } from '@raptor/shared';

// Derive the react-hook-form validation schema for a given issue type. Single
// source of truth — the form resolver, the tests, and any downstream shape
// checks all use this function so the config drives everything.
//
// Field-key rules:
//   - `title` and `product` are always required (unrelated to per-type config).
//   - Non-markdown fields contribute one key each, keyed by field.id.
//   - markdown fields are presentation-only and contribute nothing.
//   - dropdown fields validate as z.enum against their options; if a config
//     accidentally ships an empty options list we fall back to a required
//     string with a descriptive message rather than crashing zod (which
//     requires a non-empty tuple).
export function buildIssueSchema(type: IssueTypeConfig) {
  const shape: Record<string, ZodTypeAny> = {
    title: z.string().trim().min(1, 'Title is required'),
    product: z.string().min(1, 'Product is required'),
  };

  for (const field of type.fields) {
    if (field.kind === 'markdown') continue;
    if (!field.id) continue;

    if (field.kind === 'dropdown') {
      const opts = field.options ?? [];
      if (opts.length === 0) {
        shape[field.id] = z
          .string()
          .min(1, `${field.label} has no configured options — contact an administrator`);
      } else {
        shape[field.id] = z.enum(opts as [string, ...string[]], {
          errorMap: () => ({ message: `${field.label} is required` }),
        });
      }
      continue;
    }

    // input | textarea
    if (field.required) {
      shape[field.id] = z.string().trim().min(1, `${field.label} is required`);
    } else {
      shape[field.id] = z.string().trim().optional();
    }
  }

  return z.object(shape);
}

export type IssueFormValues = Record<string, string>;
